import { c as _c } from "react/compiler-runtime";
import { useDataPart } from '@ai-sdk-tools/store';
import type { CoverageGapAnalysisData, PolicyCheckData } from '@newfront-insurance/ai-api';
import type { UIDataTypes, UIMessagePart, UITools } from 'ai';
import type { ReactNode } from 'react';
import { CoverageGapAnalysisStatusCard } from '../../customized-data/coverage-gap-analysis-status-card';
import { PolicyCheckingStatusCard } from '../../customized-data/policy-checking-status-card';
import { ProgressMessage } from '../../ui/progress-message';
import { createDefaultToolRenderers as createDefaultToolRenderersFromComponents } from '../components/tools/renderers';
import { useShowProgressDataPart } from '../hooks/use-show-progress-data-part';

// Type for tool output renderer function
export type ToolOutputRenderer = (part: UIMessagePart<UIDataTypes, UITools>, messageUuid: string) => ReactNode | null;

// Type for data part renderer - now a React component instead of a function
export type DataPartRenderer = () => ReactNode | null;

/**
 * Coverage Gap Analysis data part component.
 * Uses useDataPart hook to get the latest data.
 */
function CoverageGapAnalysisDataPart() {
  const $ = _c(2);
  const [data] = useDataPart("coverageGapAnalysis");
  if (!data) {
    return null;
  }
  let t0;
  if ($[0] !== data) {
    t0 = <CoverageGapAnalysisStatusCard data={data} />;
    $[0] = data;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}

/**
 * Policy Check data part component.
 * Uses useDataPart hook to get the latest data.
 */
function PolicyCheckDataPart() {
  const $ = _c(2);
  const [data] = useDataPart("policyCheck");
  if (!data) {
    return null;
  }
  let t0;
  if ($[0] !== data) {
    t0 = <PolicyCheckingStatusCard data={data} />;
    $[0] = data;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
export function ProgressDataPart() {
  const $ = _c(2);
  const showProgressDataPart = useShowProgressDataPart();
  const [data] = useDataPart("progress");
  if (!showProgressDataPart) {
    return null;
  }
  const t0 = data?.message ?? "";
  let t1;
  if ($[0] !== t0) {
    t1 = <ProgressMessage message={t0} />;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}

/**
 * Creates default data part renderers.
 * Apps can extend or override these by passing their own renderers to Messages component.
 * Each renderer is a React component that uses useDataPart hook internally.
 */
export function createDefaultDataPartRenderers(): Record<string, DataPartRenderer> {
  return {
    coverageGapAnalysis: CoverageGapAnalysisDataPart,
    policyCheck: PolicyCheckDataPart
  };
}

/**
 * Creates default tool output renderers.
 * Apps can extend or override these by passing their own renderers to Messages component.
 */
export function createDefaultToolRenderers(): Record<string, ToolOutputRenderer> {
  return createDefaultToolRenderersFromComponents();
}